'use client';

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { get, map } from 'lodash';
import cx from 'classnames';

import { OptionType, Placement } from './menu.type';
import Icon from '../icons';
import { getCordinates } from './helper';

import styles from './menu.module.scss';

interface Props {
  children: React.ReactNode;
  options?: OptionType[];
  placement?: Placement;
  width?: string;
  menuClassName?: string;
  menuOptionClassname?: string;
}

const Menu = (props: Props) => {
  const {
    children,
    placement = 'bottomLeft',
    options,
    width,
    menuClassName,
    menuOptionClassname,
  } = props;

  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [cordinates, setCordinates] = React.useState({
    top: 0,
    left: 0,
  });

  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // //* this will be used for keyboard navigation
  const menuItemsRefs = useMemo(
    () => map(Array(options?.length).fill(0), () => React.createRef<HTMLDivElement>()),
    [options?.length],
  );
  // //* to keep track the current hoverd item from keyboard
  const currentHoveredItem = useRef(-1);

  const handleKeydown = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMenuOpen(false);
        triggerRef.current?.blur();
      } else if (e.key === 'ArrowDown') {
        const prevItemIndex = currentHoveredItem.current;
        const currentItemIndex = (currentHoveredItem.current + 1) % (options?.length || 1);

        currentHoveredItem.current = currentItemIndex;

        if (prevItemIndex !== -1) {
          const prevItemRef = menuItemsRefs[prevItemIndex].current;
          if (prevItemRef) prevItemRef.classList.remove(styles.hoveredClass);
        }
        if (currentItemIndex < (options?.length || 1)) {
          const currentItemRef = menuItemsRefs[currentItemIndex].current;
          if (currentItemRef) currentItemRef.classList.add(styles.hoveredClass);
        }
      } else if (e.key === 'ArrowUp') {
        const prevItemIndex = currentHoveredItem.current;
        const currentItemIndex =
          (currentHoveredItem.current - 1 + (options?.length || 1)) % (options?.length || 1);

        currentHoveredItem.current = currentItemIndex;

        if (prevItemIndex !== -1) {
          const prevItemRef = menuItemsRefs[prevItemIndex].current;
          if (prevItemRef) prevItemRef.classList.remove(styles.hoveredClass);
        }
        if (currentItemIndex < (options?.length || 1)) {
          const currentItemRef = menuItemsRefs[currentItemIndex].current;
          if (currentItemRef) currentItemRef.classList.add(styles.hoveredClass);
        }
      } else if (e.key === 'Enter') {
        if (currentHoveredItem.current !== -1) {
          const currentItemRef = menuItemsRefs[currentHoveredItem.current].current;
          setIsMenuOpen(false);
          if (currentItemRef) currentItemRef.click();
        }
      }
    },
    [options, menuItemsRefs],
  );

  //* subscribe to for keyboard events
  useEffect(() => {
    if (isMenuOpen) {
      document.addEventListener('keydown', handleKeydown);
    } else {
      document.removeEventListener('keydown', handleKeydown);
    }
    return () => {
      document.removeEventListener('keydown', handleKeydown);
    };
  }, [isMenuOpen, handleKeydown]);

  useLayoutEffect(() => {
    if (!contentRef.current || !triggerRef.current) return;
    const {
      height: contentHeight,
      width: contentWidth,
      x,
      y,
    } = contentRef.current.getBoundingClientRect();
    const { height, width } = triggerRef.current.getBoundingClientRect();
    const { left, top } = getCordinates(
      height,
      width,
      contentHeight,
      contentWidth,
      placement,
      x,
      y,
    );

    setCordinates({
      top,
      left,
    });
  }, [isMenuOpen, placement]);

  return (
    <div className={styles.menuContainer}>
      <button
        className={styles.button}
        ref={triggerRef}
        onBlur={() => setIsMenuOpen(false)}
        onClick={(e) => {
          e.stopPropagation();
          setIsMenuOpen((prev) => !prev);
        }}
      >
        {children}
        <div
          className={cx(styles.Menu, menuClassName)}
          ref={contentRef}
          style={{
            top: cordinates.top,
            left: cordinates.left,
            display: isMenuOpen ? 'block' : 'none',
            width: width,
          }}
        >
          {map(options, (option, index) => (
            <div
              key={option.value}
              className={cx(styles.optionContainer, menuOptionClassname, {
                [styles.disabled]: option.disabled,
              })}
              onClick={option?.onClick}
              ref={menuItemsRefs[index]}
            >
              {get(option, 'icon', '') && (
                <div className={styles.iconContainer}>
                  <Icon icon={option.icon} />
                </div>
              )}
              <p className={styles.labelContainer}>{option.label}</p>
            </div>
          ))}
        </div>
      </button>
    </div>
  );
};

export default Menu;
